import { useEffect, useState, useContext, lazy, Suspense } from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import Header from "./_components/header/header";
import Main from "./pages/main";
import ApiService from "./_services/ApiService";
import googleAnalytics from "./_services/google-analytics/google-analytics.service";
import Config from "./_config/config";
import { AppPropertiesContext } from "./contexts/app-properties.context";
import Loader from "./_components/loader/loader";
import SideNavigation from "./_components/side-navigation/side-navigation.component";
import { AutoLogoutProvider } from "./contexts/auto-logout.context";
import isKiosk from "./_services/kiosk/kiosk.service";
import { applyTheme } from "./_config/themeConfig";

// Initialize i18n
import "./i18n";

import "./App.css";

// Kiosk V2 shell — self-contained, does NOT use any existing kiosk components
const KioskApp = lazy(() => import("./pages/kiosk-app/KioskApp"));

// Saarthi — career guidance sub-app for Class 12 students
const SaarthiApp = lazy(() => import("./pages/saarthi-app/SaarthiApp"));

// Public credential verification — standalone, no auth
const CredentialVerify = lazy(() => import("./pages/credential-verify/CredentialVerify"));

// Public Voila Board — standalone, no auth
const PublicVoilaBoard = lazy(() => import("./pages/voila-board/PublicVoilaBoard"));

function App() {
  const {
    addDataToTests,
    addRegistrationForms,
    addHideStudentDossier,
    addLoginText1,
    addAppProperties,
  } = useContext(AppPropertiesContext);
  const [loginRoute, setloginRoute] = useState("");
  const [homeRoute, setHomeRoute] = useState("");
  const [suppressLogs, setSuppressLogs] = useState(null);
  const [loading, setLoading] = useState(true);

  const initGoogleAnalytics = async () => {
    try {
      setLoading(true);
      const res = await ApiService.fetchGoogleAnalytics();
      console.log(res);
      const appProperties =
        typeof res.data === "string" ? JSON.parse(res.data || "{}") : res.data;

      const {
        "GA-Code": id,
        "Home-Route": homeRoute,
        "Login-Route": loginRoute,
        "Home-Title": homeTitle,
        "Suppress-Home-Page-Logs": suppressLogs,
        "Mobile-Login-Footer": loginFooter,
        "Portlet-Url": testCacheUrl,
        "Registration-Forms": registrationForms,
        "Hide-Student-Dossier": hideStudentDossier,
        sebBrowserHomeRoute,
        Tests,
        "Login-Text1": loginText1,
      } = appProperties;

      if (loginText1) {
        addLoginText1(loginText1);
      }

      if (Tests) {
        addDataToTests(Tests);
      }

      if (appProperties) {
        addAppProperties(appProperties);
      }

      addHideStudentDossier(hideStudentDossier ? true : false);

      console.log(id, homeRoute);
      console.log(homeTitle, suppressLogs);

      console.log(registrationForms, "form");

      document.title = homeTitle || Config.siteTitle;
      googleAnalytics(id);
      setloginRoute(loginRoute);
      setHomeRoute(homeRoute);
      if (homeRoute) sessionStorage.setItem("homeRoute", homeRoute);
      if (sebBrowserHomeRoute) sessionStorage.setItem("sebBrowserHomeRoute", sebBrowserHomeRoute);
      if (loginRoute) sessionStorage.setItem("loginRoute", loginRoute);
      if (loginFooter) sessionStorage.setItem("loginFooter", loginFooter);
      if (testCacheUrl) sessionStorage.setItem("testCacheUrl", testCacheUrl);
      if (homeTitle) sessionStorage.setItem("homeTitle", homeTitle);
      if (registrationForms) addRegistrationForms(registrationForms);
      if (suppressLogs !== undefined) setSuppressLogs(suppressLogs);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const updateFavIcon = () => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }
    const url = `https://tuningfork-live.s3.ap-southeast-1.amazonaws.com/${Config.siteTitle}/files/logos/favicon.ico`;
    console.log(url);
    link.href = url;
  };

  const updateBodyBackground = () => {
    document.body.style = `background:url(https://tuningfork-live.s3.ap-southeast-1.amazonaws.com/${Config.siteTitle}/files/logos/watermark1.jpg)`;
  };

  const addPaymentRecordsShortcut = () => {
    if (!isKiosk()) return;
    document.addEventListener("keydown", function (e) {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault(); // Prevent default browser behavior if any
        // Add your custom logic here
        window.location = "/payment-records";
      }
    });
  };

  const checkValidUser = async () => {
    // Saarthi sub-app uses its own auth flow — skip main-app user verification
    if (window.location.pathname.startsWith('/saarthi')) return;
    // Voila Board public view — no auth required
    if (window.location.pathname.startsWith('/voila/')) return;

    if(!localStorage.getItem("user")) return;

    try {
      const response = await ApiService.verifyValidUser();
      console.log("verifyUser response.data:", JSON.stringify(response.data));
      if (response.data.message?.toLowerCase() === "success" && response.data.data) {
        return;
      }

      if(window.location.pathname === "/logout") return;
      return (window.location = "/logout");
    } catch (err) {
      console.log(err);
      return (window.location = "/logout");
    }
  };

  useEffect(() => {
    applyTheme();
    checkValidUser();
    initGoogleAnalytics();
    updateFavIcon();
    updateBodyBackground();
    addPaymentRecordsShortcut();
  }, []);

  return (
    <div id="App">
      <div id="page-wrap">
        <div className="container1">
          <Header />
          <Router>
            <Switch>
              {/* Public credential verification — no auth, no app shell */}
              <Route path="/verify/:credentialId">
                <Suspense fallback={<Loader />}>
                  <CredentialVerify />
                </Suspense>
              </Route>

              {/* Public Voila Board — no auth, standalone timeline view */}
              <Route path="/voila/:boardUUID">
                <Suspense fallback={<Loader />}>
                  <PublicVoilaBoard />
                </Suspense>
              </Route>

              {/* Saarthi — career guidance sub-app, bypasses main shell */}
              <Route path="/saarthi">
                <Suspense fallback={<Loader />}>
                  <SaarthiApp />
                </Suspense>
              </Route>

              {/* Kiosk V2 shell — bypasses Header, SideNav, and production AutoLogout */}
              <Route path="/kiosk">
                <Suspense fallback={<Loader />}>
                  <KioskApp />
                </Suspense>
              </Route>

              {/* Regular app — unchanged */}
              <Route>
                <AutoLogoutProvider>
                  {loading ? (
                    <Loader />
                  ) : (
                    <SideNavigation>
                      <Main
                        loginRoute={loginRoute}
                        homeRoute={homeRoute}
                        suppressLogs={suppressLogs}
                      />
                    </SideNavigation>
                  )}
                </AutoLogoutProvider>
              </Route>
            </Switch>
          </Router>
        </div>
      </div>
    </div>
  );
}

export default App;
